/**
 * ALD — Référentiel des 30 Affections Longue Durée
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HeartHandshake, Search, TrendingUp, Building2, ArrowRight, BookOpen } from "lucide-react";
import { ALD_ENTRIES } from "@/lib/referential/ald";

export const Route = createFileRoute("/referential/ald")({
  component: ALDReferential,
});

function ALDReferential() {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");

  const categories = useMemo(() => Array.from(new Set(ALD_ENTRIES.map((e) => e.category))).sort(), []);
  const filtered = useMemo(() => ALD_ENTRIES.filter((e) => {
    if (filterCat !== "all" && e.category !== filterCat) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return e.code.toLowerCase().includes(s) || e.label.toLowerCase().includes(s) || e.relatedIcd10.some((c) => c.toLowerCase().includes(s));
  }), [search, filterCat]);

  const top10 = useMemo(() => [...ALD_ENTRIES].sort((a, b) => b.prevalence - a.prevalence).slice(0, 10), []);
  const totalPrevalence = useMemo(() => ALD_ENTRIES.reduce((s, e) => s + e.prevalence, 0), []);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <HeartHandshake className="h-5 w-5" /> ALD — Affections Longue Durée
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Référentiel CNSS/CNOPS/AMO · {ALD_ENTRIES.length} ALD · Couverture 100% (exonération ticket modérateur)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">ALD référencées</div>
            <div className="mt-1 text-2xl font-bold">{ALD_ENTRIES.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Catégories</div>
            <div className="mt-1 text-2xl font-bold">{categories.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Prévalence cumulée</div>
            <div className="mt-1 text-2xl font-bold">~{Math.round(totalPrevalence / 1000)}k/100k</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Couverture</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">100%</div>
            <div className="text-xs text-muted-foreground">CNSS / CNOPS / AMO</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4" /> Top 10 par prévalence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5">
            {top10.map((e) => (
              <li key={e.code} className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.02] p-2 text-sm">
                <code className="font-mono font-bold w-20 shrink-0 text-xs">{e.code}</code>
                <span className="flex-1 truncate">{e.label}</span>
                <Badge variant="outline" className="text-[10px]">{e.category}</Badge>
                <span className="text-xs text-muted-foreground font-mono w-16 text-right">{e.prevalence}/100k</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (code, libellé, CIM-10)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="h-9 rounded-md border border-white/10 bg-white/5 px-3 text-sm"
        >
          <option value="all">Toutes catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="space-y-1.5">
        {filtered.map((e) => (
          <Card key={e.code}>
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <code className="font-mono font-bold w-20 shrink-0 text-sm">{e.code}</code>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{e.label}</div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-[10px]">{e.category}</Badge>
                    <span>·</span>
                    <span>CIM-10:</span>
                    {e.relatedIcd10.map((c) => (
                      <code key={c} className="text-[10px] bg-white/5 px-1 rounded">{c}</code>
                    ))}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-[10px] text-muted-foreground">CNSS</div>
                  <div className="font-bold text-emerald-400">{e.cnssCoverage}%</div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-[10px] text-muted-foreground">CNOPS</div>
                  <div className="font-bold text-emerald-400">{e.cnopsCoverage}%</div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-[10px] text-muted-foreground">AMO</div>
                  <div className="font-bold text-emerald-400">{e.amoCoverage}%</div>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/ald">
                    Voir <ArrowRight className="h-3 w-3 ml-0.5" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
